"use client";

import React from "react";
import Button from "../button";
import Spinner from "../spinner/loading";
import type { AsaasKycStatus } from "@/types";

interface BillingKycStatusCardProps {
  status: AsaasKycStatus | null;
  loading: boolean;
  onRefresh: () => Promise<void>;
}

const humanizeKey = (key: string): string =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

export const BillingKycStatusCard: React.FC<BillingKycStatusCardProps> = ({
  status,
  loading,
  onRefresh,
}) => {
  const primaryStatus =
    status?.status || status?.description || "Não disponível";
  const secondaryStatus = status?.subStatus || status?.detail || status?.reason;

  const otherEntries = status
    ? Object.entries(status).filter(
        ([key]) =>
          ![
            "status",
            "subStatus",
            "sub_status",
            "detail",
            "description",
            "reason",
          ].includes(key),
      )
    : [];

  return (
    <section className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-indigo-900">
            Situação cadastral
          </h3>
          <p className="text-xs text-indigo-700">
            Consulte o status mais recente enviado pelo provedor bancário.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void onRefresh()}
          disabled={loading}
          text={loading ? "Consultando..." : "Atualizar status"}
        />
      </header>

      <div className="mt-4 rounded-lg border border-indigo-100 bg-white p-4 shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-3 text-sm text-indigo-900">
            <div>
              <span className="text-xs uppercase text-indigo-500">
                Status principal
              </span>
              <p className="text-base font-semibold">{primaryStatus}</p>
            </div>

            {secondaryStatus && (
              <div>
                <span className="text-xs uppercase text-indigo-500">
                  Detalhes
                </span>
                <p>{secondaryStatus}</p>
              </div>
            )}

            {otherEntries.length > 0 && (
              <div className="space-y-2">
                {otherEntries.map(([key, value]) => {
                  if (value === null || value === undefined || value === "") {
                    return null;
                  }
                  return (
                    <div
                      key={key}
                      className="rounded border border-indigo-100 bg-indigo-50/60 p-2"
                    >
                      <span className="text-[11px] uppercase text-indigo-500">
                        {humanizeKey(key)}
                      </span>
                      <p className="text-xs text-indigo-800">
                        {typeof value === "string"
                          ? value
                          : JSON.stringify(value)}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {!secondaryStatus && otherEntries.length === 0 && (
              <p className="text-xs text-indigo-600">
                Nenhuma informação adicional disponível no momento.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default BillingKycStatusCard;
